import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import * as FileDetailsActions from './file-details.actions';
import { FileDetailsRepository } from '../../infrastructure/http/repositories/file-details.repository';

@Injectable()
export class FileDetailsEffects {
  private readonly actions$ = inject(Actions);
  private readonly repository = inject(FileDetailsRepository);

  loadFileDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileDetailsActions.loadFileDetails),
      switchMap(({ id }) =>
        this.repository.getById(id).pipe(
          map(file => {
            if (!file) {
              return FileDetailsActions.loadFileDetailsFailure({
                error: 'File not found',
              });
            }

            return FileDetailsActions.loadFileDetailsSuccess({ file });
          }),
          catchError(error =>
            of(
              FileDetailsActions.loadFileDetailsFailure({
                error: this.getErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  loadFileStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileDetailsActions.loadFileStatuses),
      switchMap(() =>
        this.repository.getFileStatuses().pipe(
          map(statuses =>
            FileDetailsActions.loadFileStatusesSuccess({ statuses })
          ),
          catchError(error =>
            of(
              FileDetailsActions.loadFileStatusesFailure({
                error: this.getErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  updateFileStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileDetailsActions.updateFileStatus),
      switchMap(({ id, fileStatusId }) =>
        this.repository.updateStatus(id, fileStatusId).pipe(
          map(() =>
            FileDetailsActions.updateFileStatusSuccess({ id, fileStatusId })
          ),
          catchError(error =>
            of(
              FileDetailsActions.updateFileStatusFailure({
                error: this.getErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (
      error &&
      typeof error === 'object' &&
      'error' in error &&
      error.error &&
      typeof error.error === 'object' &&
      'message' in error.error
    ) {
      return (error.error as { message: string }).message;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return (error as { message: string }).message;
    }
    return 'Something went wrong';
  }
}
